/**
 * Wheel Renderer Hybrid - Premium Casino Look
 * 
 * Architecture:
 * - 16 PNG layers for premium visuals (rim, bulbs, gloss, shadows, center, pointer)
 * - Dynamic PIXI.Graphics segments (merchant colors, count, weights)
 * - Strict z-order layering
 * - Split: spinning container (segments) vs static container (rim, bulbs, pointer)
 */

import * as PIXI from 'pixi.js';
import type { WheelConfig, IconKey } from '../runtime/types';
import { type IconTextures, parseIconColor } from './iconLoader';

/**
 * Texture map for all PNG layers (22 total)
 */
export interface WheelTextures {
  shadow: PIXI.Texture;
  rimBase: PIXI.Texture;
  rimHighlight: PIXI.Texture;
  bulbsOff: PIXI.Texture;
  bulbsOn: PIXI.Texture;
  centerBase: PIXI.Texture;
  centerGloss: PIXI.Texture;
  pointer: PIXI.Texture;
  segmentsBase: PIXI.Texture;
  segmentsGloss: PIXI.Texture;
  innerShadowRing: PIXI.Texture;
  centerShadow: PIXI.Texture;
  centerInnerHighlight: PIXI.Texture;
  segmentSeparators: PIXI.Texture;
  textShadowEmboss: PIXI.Texture;
  wheelAmbientShadow: PIXI.Texture;
  // NEW: Center button detailed layers (17-22)
  centerShadowOuter: PIXI.Texture;
  centerRingGold: PIXI.Texture;
  centerFaceBase: PIXI.Texture;
  centerFaceInnerShadow: PIXI.Texture;
  centerGlassHighlight: PIXI.Texture;
  centerInnerHighlightV2: PIXI.Texture;
}

/**
 * Color utilities
 */
/* TEMP DISABLED - only needed for procedural segments
function parseColor(color: string): number {
  return parseInt(color.replace('#', ''), 16);
}
*/

// Removed getLuminance and getContrastColor - not needed in hybrid renderer

export class WheelRendererHybrid extends PIXI.Container {
  private config: WheelConfig;
  private textures: WheelTextures;
  private iconTextures: IconTextures = {};
  private wheelRadius: number;
  
  // Container split: bottom static, spinning, top static
  private bottomStaticContainer: PIXI.Container;
  private spinContainer: PIXI.Container;
  private topStaticContainer: PIXI.Container;
  
  // Layer containers (strict z-order)
  private ambientLayer: PIXI.Container;
  private shadowLayer: PIXI.Container;
  private segmentsLayer: PIXI.Container;
  private segmentsBaseOverlayLayer: PIXI.Container;
  private separatorsLayer: PIXI.Container;
  private segmentsGlossLayer: PIXI.Container;
  private rimBaseLayer: PIXI.Container;
  private bulbsLayer: PIXI.Container;
  private rimHighlightLayer: PIXI.Container;
  private innerShadowRingLayer: PIXI.Container;
  private textEmbossLayer: PIXI.Container;
  private iconsLayer: PIXI.Container;
  private centerShadowLayer: PIXI.Container;
  private centerBaseLayer: PIXI.Container;
  private centerInnerHighlightLayer: PIXI.Container;
  private centerGlossLayer: PIXI.Container;
  private pointerLayer: PIXI.Container;
  
  // Bulb sprites for animation
  private bulbsOffSprite: PIXI.Sprite | null = null;
  private bulbsOnSprite: PIXI.Sprite | null = null;
  
  // Center text (editable)
  private centerText: PIXI.Text | null = null;

  constructor(config: WheelConfig, textures: WheelTextures, radius: number) {
    super();

    this.config = config;
    this.textures = textures;
    this.wheelRadius = radius;

    // Create main containers
    this.bottomStaticContainer = new PIXI.Container();
    this.spinContainer = new PIXI.Container();
    this.topStaticContainer = new PIXI.Container();

    // Create all layer containers
    this.ambientLayer = new PIXI.Container();
    this.shadowLayer = new PIXI.Container();
    this.segmentsLayer = new PIXI.Container();
    this.segmentsBaseOverlayLayer = new PIXI.Container();
    this.separatorsLayer = new PIXI.Container();
    this.segmentsGlossLayer = new PIXI.Container();
    this.rimBaseLayer = new PIXI.Container();
    this.bulbsLayer = new PIXI.Container();
    this.rimHighlightLayer = new PIXI.Container();
    this.innerShadowRingLayer = new PIXI.Container();
    this.textEmbossLayer = new PIXI.Container();
    this.iconsLayer = new PIXI.Container();
    this.centerShadowLayer = new PIXI.Container();
    this.centerBaseLayer = new PIXI.Container();
    this.centerInnerHighlightLayer = new PIXI.Container();
    this.centerGlossLayer = new PIXI.Container();
    this.pointerLayer = new PIXI.Container();

    // Add layers to containers in correct order
    this.buildSceneGraph();
    
    // Render all layers
    this.render();
  }

  /**
   * Build scene graph with strict z-order (bottom to top)
   */
  private buildSceneGraph(): void {
    // ========== BOTTOM STATIC (under spinning wheel) ==========
    this.bottomStaticContainer.addChild(this.ambientLayer);        // 1. Ambient shadow (furthest back)
    this.bottomStaticContainer.addChild(this.shadowLayer);         // 2. Drop shadow (under wheel)
    
    // ========== SPINNING (rotates with wheel) ==========
    this.spinContainer.addChild(this.segmentsLayer);               // 3. Segments (dynamic colors)
    this.spinContainer.addChild(this.segmentsBaseOverlayLayer);    // 4. Segments base PNG overlay
    this.spinContainer.addChild(this.separatorsLayer);             // 5. Golden separators
    this.spinContainer.addChild(this.segmentsGlossLayer);          // 6. Gloss on segments
    this.spinContainer.addChild(this.innerShadowRingLayer);        // 7. Inner shadow ring (depth)
    this.spinContainer.addChild(this.iconsLayer);                  // 8. Segment icons
    this.spinContainer.addChild(this.textEmbossLayer);             // 9. Text emboss (rotates with segments)
    
    // ========== TOP STATIC (above spinning wheel) ==========
    this.topStaticContainer.addChild(this.rimBaseLayer);           // 9. Golden rim base
    this.topStaticContainer.addChild(this.bulbsLayer);             // 10. Bulb lights
    this.topStaticContainer.addChild(this.rimHighlightLayer);      // 11. Rim highlight/shine
    this.topStaticContainer.addChild(this.centerShadowLayer);      // 12. Center button shadow
    this.topStaticContainer.addChild(this.centerBaseLayer);        // 13. Center button base
    this.topStaticContainer.addChild(this.centerInnerHighlightLayer); // 14. Center inner highlight
    this.topStaticContainer.addChild(this.centerGlossLayer);       // 15. Center gloss
    this.topStaticContainer.addChild(this.pointerLayer);           // 16. Pointer (top-most)

    // Add main containers to root IN CORRECT ORDER
    // TEMP: Spin container on TOP for debugging PNG layers
    this.addChild(this.bottomStaticContainer);  // First (back)
    this.addChild(this.topStaticContainer);     // Middle (rim, center, etc.)
    this.addChild(this.spinContainer);          // Last (spinning segments - ON TOP)
  }

  /**
   * Render all layers - INCREMENTAL BUILD APPROACH
   * Following ChatGPT z-order recommendations
   */
  private render(): void {
    // Clear all layers
    this.clearAllLayers();

    // ========== PHASE 1: FOUNDATION ✅ ==========
    // Static layers (do not rotate)
    this.renderRimBase();               // 1. Golden rim structure

    // Spinning layers (rotate with wheel)
    this.renderSegmentsBaseOverlay();   // 2. Segments base (gradients)
    
    // ========== PHASE 2: BULBS + RIM HIGHLIGHT ✅ ==========
    // Static layers (do not rotate)
    this.renderBulbs();                 // 3. Bulbs OFF + ON (animated)
    this.renderRimHighlight();          // 4. Rim metallic highlight
    
    // ========== PHASE 3: GLOSS + INNER SHADOW ==========
    // Spinning layers (rotate with wheel, same scale as segments base)
    // NOTE: Separators now embedded in 09_segments_base.png (combined layer)
    this.renderSegmentsGloss();         // 5. Glossy shine on segments
    // DISABLED: Inner shadow ring creates white artifacts outside wheel
    // this.renderInnerShadowRing();       // 6. Inner shadow ring (depth)
    this.renderSegmentIcons();            // 7. Segment icons (tinted)
    
    // POINTER (static, top-most visual element)
    this.renderPointer();               // 8. Golden pointer at top
    
    // DEBUG disabled - wheel is now properly centered!
    // this.renderDebugCenter();
    
    // TODO Phase 4: Center button (6 layers, static)
    // TODO Phase 5: Pointer (static, top-most)
  }

  /**
   * DEBUG: Render center crosshair and radius circles
   */
  private renderDebugCenter(): void {
    const debug = new PIXI.Graphics();
    
    // Center crosshair (red)
    debug.moveTo(-20, 0);
    debug.lineTo(20, 0);
    debug.stroke({ color: 0xff0000, width: 2 });
    
    debug.moveTo(0, -20);
    debug.lineTo(0, 20);
    debug.stroke({ color: 0xff0000, width: 2 });
    
    // Wheel radius circle (green)
    debug.circle(0, 0, this.wheelRadius);
    debug.stroke({ color: 0x00ff00, width: 2 });
    
    // Bulbs radius circle (blue)
    debug.circle(0, 0, this.wheelRadius + 22);
    debug.stroke({ color: 0x0000ff, width: 2 });
    
    // Segments radius circle (yellow)
    debug.circle(0, 0, this.wheelRadius - 10);
    debug.stroke({ color: 0xffff00, width: 2 });
    
    this.addChild(debug);
  }

  /**
   * Clear all layers
   */
  private clearAllLayers(): void {
    this.ambientLayer.removeChildren();
    this.shadowLayer.removeChildren();
    this.segmentsLayer.removeChildren();
    this.segmentsBaseOverlayLayer.removeChildren();
    this.separatorsLayer.removeChildren();
    this.segmentsGlossLayer.removeChildren();
    this.rimBaseLayer.removeChildren();
    this.bulbsLayer.removeChildren();
    this.rimHighlightLayer.removeChildren();
    this.innerShadowRingLayer.removeChildren();
    this.iconsLayer.removeChildren();
    this.textEmbossLayer.removeChildren();
    this.centerShadowLayer.removeChildren();
    this.centerBaseLayer.removeChildren();
    this.centerInnerHighlightLayer.removeChildren();
    this.centerGlossLayer.removeChildren();
    this.pointerLayer.removeChildren();
    
    this.bulbsOffSprite = null;
    this.bulbsOnSprite = null;
    this.centerText = null;
  }

  /**
   * Calculate sprite scale for 2048x2048 textures
   * PNG canvas radius = 1024
   * Target radius = wheelRadius + padding for rim
   */
  /**
   * Get scale for RIM/BULBS (static layers that extend beyond wheel)
   */
  private getSpriteScale(): number {
    // Rim extends beyond wheelRadius
    const targetOuterRadius = this.wheelRadius + 20; // Rim padding
    const pngRadius = 512; // PNG canvas radius (1024x1024 PNG, radius = 512)
    return targetOuterRadius / pngRadius;
  }

  /**
   * Get scale for BULBS specifically (slightly larger to be fully visible)
   */
  private getBulbsScale(): number {
    // Bulbs need +2% more scale to be 100% visible above segments
    const targetOuterRadius = this.wheelRadius + 22; // +2px more than rim
    const pngRadius = 512; // PNG radius (1024x1024, radius = 512)
    return targetOuterRadius / pngRadius;
  }

  /**
   * Get scale for SEGMENTS (spinning layers INSIDE the rim)
   * Must be SMALLER to leave space for bulbs/rim
   */
  private getSegmentsScale(): number {
    // Segments must stay INSIDE the rim
    const targetOuterRadius = this.wheelRadius - 10; // Pull inward by 10px
    const pngRadius = 512; // PNG canvas radius (1024 width, radius = 512)
    return targetOuterRadius / pngRadius;
  }

  /**
   * Create centered sprite from texture
   */
  private createCenteredSprite(texture: PIXI.Texture, scale?: number): PIXI.Sprite {
    const sprite = new PIXI.Sprite(texture);
    sprite.anchor.set(0.5);
    sprite.x = 0;
    sprite.y = 0;
    if (scale !== undefined) {
      sprite.scale.set(scale);
    } else {
      sprite.scale.set(this.getSpriteScale());
    }
    return sprite;
  }

  // ============================================================================
  // Static Layers (PNG overlays)
  // ============================================================================

  private renderAmbientShadow(): void {
    const sprite = this.createCenteredSprite(this.textures.wheelAmbientShadow);
    sprite.blendMode = 'multiply';
    sprite.alpha = 0.6;
    this.ambientLayer.addChild(sprite);
  }

  private renderShadow(): void {
    const sprite = this.createCenteredSprite(this.textures.shadow);
    sprite.blendMode = 'multiply';
    sprite.alpha = 0.4;
    this.shadowLayer.addChild(sprite);
  }

  private renderRimBase(): void {
    // WRAPPER PATTERN: 02_rim_base_gold.png is 1024x1024
    // PRECISE measurement (alpha > 200):
    //   Visual center: (513, 454), Math center: (512, 512)
    //   Visual is 58px ABOVE math center
    const scale = this.getSpriteScale();
    const pngOffsetX = -1;
    const pngOffsetY = 58; // Move DOWN to center
    
    const wrapper = new PIXI.Container();
    wrapper.position.set(pngOffsetX * scale, pngOffsetY * scale);
    
    const sprite = new PIXI.Sprite(this.textures.rimBase);
    sprite.anchor.set(0.5, 0.5);
    sprite.position.set(0, 0);
    sprite.scale.set(scale);
    
    wrapper.addChild(sprite);
    this.rimBaseLayer.addChild(wrapper);
  }

  private renderBulbs(): void {
    // WRAPPER PATTERN: 04_bulbs_off/on.png are 1536x1024
    // PRECISE measurement (alpha > 200):
    //   Visual center: (768.5, 475.5), Math center: (768, 512)
    //   Visual is 36.5px ABOVE math center
    const bulbsScale = this.getBulbsScale();
    const pngOffsetX = -0.5;
    const pngOffsetY = 36.5; // Move DOWN to center
    
    const wrapper = new PIXI.Container();
    wrapper.position.set(pngOffsetX * bulbsScale, pngOffsetY * bulbsScale);
    
    // OFF sprite (base, always visible)
    this.bulbsOffSprite = new PIXI.Sprite(this.textures.bulbsOff);
    this.bulbsOffSprite.anchor.set(0.5, 0.5);
    this.bulbsOffSprite.position.set(0, 0);
    this.bulbsOffSprite.scale.set(bulbsScale);
    this.bulbsOffSprite.alpha = 1;
    wrapper.addChild(this.bulbsOffSprite);

    // ON sprite (animated alpha for breathing effect)
    this.bulbsOnSprite = new PIXI.Sprite(this.textures.bulbsOn);
    this.bulbsOnSprite.anchor.set(0.5, 0.5);
    this.bulbsOnSprite.position.set(0, 0);
    this.bulbsOnSprite.scale.set(bulbsScale);
    this.bulbsOnSprite.alpha = 0.2; // Start dim
    wrapper.addChild(this.bulbsOnSprite);
    
    this.bulbsLayer.addChild(wrapper);
  }

  private renderRimHighlight(): void {
    // WRAPPER PATTERN: 03_rim_highlight.png is 1536x1024
    // PRECISE measurement (alpha > 200):
    //   Visual center: (774, 485.5), Math center: (768, 512)
    //   Visual is 26.5px ABOVE math center
    const scale = this.getSpriteScale();
    const pngOffsetX = -6;
    const pngOffsetY = 26.5; // Move DOWN to center
    
    const wrapper = new PIXI.Container();
    wrapper.position.set(pngOffsetX * scale, pngOffsetY * scale);
    
    const sprite = new PIXI.Sprite(this.textures.rimHighlight);
    sprite.anchor.set(0.5, 0.5);
    sprite.position.set(0, 0);
    sprite.scale.set(scale);
    sprite.blendMode = 'screen';
    sprite.alpha = 0.8;
    
    wrapper.addChild(sprite);
    this.rimHighlightLayer.addChild(wrapper);
  }

  private _renderCenterShadow_DISABLED(): void {
    const sprite = this.createCenteredSprite(this.textures.centerShadow);
    sprite.blendMode = 'multiply';
    sprite.alpha = 0.5;
    this.centerShadowLayer.addChild(sprite);
  }

  private _renderCenterBase_DISABLED(): void {
    const sprite = this.createCenteredSprite(this.textures.centerBase);
    this.centerBaseLayer.addChild(sprite);
  }

  private _renderCenterInnerHighlight_DISABLED(): void {
    const sprite = this.createCenteredSprite(this.textures.centerInnerHighlight);
    sprite.blendMode = 'screen';
    sprite.alpha = 0.7;
    this.centerInnerHighlightLayer.addChild(sprite);
  }

  private _renderCenterGloss_DISABLED(): void {
    const sprite = this.createCenteredSprite(this.textures.centerGloss);
    sprite.blendMode = 'screen';
    sprite.alpha = 0.6;
    this.centerGlossLayer.addChild(sprite);
  }

  /* TEMP DISABLED FOR PNG LAYER DEBUGGING
  private _renderPointer_OLD(): void {
    const sprite = this.createCenteredSprite(this.textures.pointer);
    // Pointer should point downward (towards top of wheel)
    sprite.y = -(this.wheelRadius + 30); // Position above wheel
    this.pointerLayer.addChild(sprite);
  }
  */

  private renderPointer(): void {
    // 08b_pointer_gold_base.png: 1024x1536
    // Content height: 752px (pointing down)
    // FIXES: 1) ABSOLUTE TOP layer, 2) 3x smaller, 3) higher position
    
    const sprite = new PIXI.Sprite(this.textures.pointer);
    
    // Scale pointer - REDUCED 3x from original
    const pointerScale = (this.wheelRadius * 0.8) / 376 / 3;
    sprite.scale.set(pointerScale);
    
    // Anchor at TOP center of pointer
    sprite.anchor.set(0.5, 0);
    
    // Position: at wheel top edge, pointing down
    const pointerVisualHeight = 752 * pointerScale;
    sprite.position.set(0, -(this.wheelRadius + pointerVisualHeight * 0.3));
    
    // Add directly to ROOT container (this) as LAST child = TOP-MOST
    this.addChild(sprite);
  }

  // ============================================================================
  // Spinning Layers
  // ============================================================================

  /* TEMP DISABLED FOR PNG LAYER DEBUGGING
  private renderSegments(): void {
    const segments = this.config.segments;
    const totalWeight = segments.reduce((sum, seg) => sum + seg.weight, 0);
    let currentAngle = -Math.PI / 2; // Start at top

    segments.forEach((segment) => {
      const segmentAngle = (segment.weight / totalWeight) * Math.PI * 2;
      
      // Draw simple pie slice (no fake gradients)
      const graphics = new PIXI.Graphics();
      graphics.moveTo(0, 0);
      graphics.arc(0, 0, this.wheelRadius, currentAngle, currentAngle + segmentAngle);
      graphics.lineTo(0, 0);
      graphics.closePath();
      graphics.fill({ color: parseColor(segment.color), alpha: 1 });
      
      this.segmentsLayer.addChild(graphics);
      
      currentAngle += segmentAngle;
    });
  }
  */

  private renderSegmentsBaseOverlay(): void {
    // 09_segments_base.png is 1024x1536
    // PRECISE measurement (alpha > 200, opaque only):
    //   Math center: (512, 768)
    //   Visual center: (513.5, 732.5)
    // FINE-TUNED: moved slightly UP and RIGHT
    
    const scale = this.getSegmentsScale();
    const pngOffsetX = 0;    // Was -1.5, now 0 (slightly right)
    const pngOffsetY = 33;   // Was 35.5, now 33 (slightly up)
    
    const wrapper = new PIXI.Container();
    wrapper.position.set(pngOffsetX * scale, pngOffsetY * scale);
    
    const sprite = new PIXI.Sprite(this.textures.segmentsBase);
    sprite.anchor.set(0.5, 0.5); // ALWAYS mathematical center
    sprite.position.set(0, 0); // ALWAYS at wrapper origin
    sprite.scale.set(this.getSegmentsScale());
    sprite.alpha = 1.0;
    
    wrapper.addChild(sprite);
    this.segmentsBaseOverlayLayer.addChild(wrapper);
  }

  private renderSeparators(): void {
    // Use SAME scale as segments base for perfect alignment
    const sprite = this.createCenteredSprite(this.textures.segmentSeparators, this.getSegmentsScale());
    sprite.alpha = 1.0; // Full opacity
    this.separatorsLayer.addChild(sprite);
  }

  private renderSegmentsGloss(): void {
    // WRAPPER PATTERN: 10_segments_gloss.png is 1536x1024
    // Measured: offset (-1, 0) - nearly centered
    const scale = this.getSegmentsScale();
    const pngOffsetX = -1;
    const pngOffsetY = 0;
    
    const wrapper = new PIXI.Container();
    wrapper.position.set(pngOffsetX * scale, pngOffsetY * scale);
    
    const sprite = new PIXI.Sprite(this.textures.segmentsGloss);
    sprite.anchor.set(0.5, 0.5); // ALWAYS mathematical center
    sprite.position.set(0, 0); // ALWAYS at wrapper origin
    sprite.scale.set(this.getSegmentsScale());
    sprite.blendMode = 'screen';
    sprite.alpha = 0.6;
    
    wrapper.addChild(sprite);
    this.segmentsGlossLayer.addChild(wrapper);
  }

  private renderInnerShadowRing(): void {
    // WRAPPER PATTERN: 11_inner_shadow_ring.png is 1536x1024
    // Measured: offset (-1, 0) - nearly centered
    const scale = this.getSegmentsScale();
    const pngOffsetX = -1;
    const pngOffsetY = 0;
    
    const wrapper = new PIXI.Container();
    wrapper.position.set(pngOffsetX * scale, pngOffsetY * scale);
    
    const sprite = new PIXI.Sprite(this.textures.innerShadowRing);
    sprite.anchor.set(0.5, 0.5); // ALWAYS mathematical center
    sprite.position.set(0, 0); // ALWAYS at wrapper origin
    sprite.scale.set(this.getSegmentsScale());
    sprite.blendMode = 'multiply';
    sprite.alpha = 0.4;
    
    wrapper.addChild(sprite);
    this.innerShadowRingLayer.addChild(wrapper);
  }

  /**
   * Render icons in each segment
   * Icons are positioned radially and tinted for contrast
   */
  private renderSegmentIcons(): void {
    const segments = this.config.segments || [];
    const numSegments = segments.length;
    if (numSegments === 0) return;

    // Calculate total weight for angle distribution
    const totalWeight = segments.reduce((sum, s) => sum + (s.weight || 1), 0);
    
    let currentAngle = -Math.PI / 2; // Start at top (12 o'clock)

    for (let i = 0; i < numSegments; i++) {
      const segment = segments[i];
      const segmentWeight = segment.weight || 1;
      const segmentAngle = (segmentWeight / totalWeight) * Math.PI * 2;
      const midAngle = currentAngle + segmentAngle / 2;

      // Check if segment has an icon configured
      const iconKey = segment.iconKey as IconKey | undefined;
      if (!iconKey || iconKey === 'none' || segment.iconEnabled === false) {
        currentAngle += segmentAngle;
        continue;
      }

      // Get icon texture
      const texture = this.iconTextures[iconKey];
      if (!texture) {
        console.warn(`[Icons] Missing texture for icon: ${iconKey}`);
        currentAngle += segmentAngle;
        continue;
      }

      // Icon configuration with defaults
      const iconSize = segment.iconSize ?? 0.12;
      const iconOffset = segment.iconOffset ?? 0.55;
      const iconColor = segment.iconColor ?? 'auto';

      // Calculate icon position along segment mid-line
      const iconRadius = this.wheelRadius * iconOffset;
      const iconX = Math.cos(midAngle) * iconRadius;
      const iconY = Math.sin(midAngle) * iconRadius;

      // Scale icon (512px source → target size)
      const targetSize = this.wheelRadius * iconSize * 2;
      const iconScale = targetSize / 512; // Updated for 512px source

      // Apply tint color
      const tintColor = parseIconColor(iconColor, segment.color);

      // === LAYER 1: Soft shadow (depth) ===
      const shadowSprite = new PIXI.Sprite(texture);
      shadowSprite.anchor.set(0.5, 0.5);
      shadowSprite.position.set(iconX + 3, iconY + 3); // Slight offset
      shadowSprite.scale.set(iconScale);
      shadowSprite.tint = 0x000000; // Black
      shadowSprite.alpha = 0.25; // Subtle
      shadowSprite.rotation = 0;
      this.iconsLayer.addChild(shadowSprite);

      // === LAYER 2: Main icon (high-contrast) ===
      const iconSprite = new PIXI.Sprite(texture);
      iconSprite.anchor.set(0.5, 0.5);
      iconSprite.position.set(iconX, iconY);
      iconSprite.scale.set(iconScale);
      iconSprite.tint = tintColor;
      iconSprite.alpha = 1.0;
      iconSprite.rotation = 0; // Always upright
      this.iconsLayer.addChild(iconSprite);

      // === LAYER 3: Subtle highlight (casino shine) ===
      const highlightSprite = new PIXI.Sprite(texture);
      highlightSprite.anchor.set(0.5, 0.5);
      highlightSprite.position.set(iconX - 1, iconY - 1); // Top-left offset
      highlightSprite.scale.set(iconScale);
      highlightSprite.tint = 0xFFFFFF; // White
      highlightSprite.alpha = 0.15; // Very subtle
      highlightSprite.rotation = 0;
      this.iconsLayer.addChild(highlightSprite);

      currentAngle += segmentAngle;
    }
  }

  // ============================================================================
  // Center Text (Editable)
  // ============================================================================

  private _renderCenterText_DISABLED(): void {
    const isLightBackground = true; // Center is pearl white in design
    const textColor = isLightBackground ? 0x1a1a1a : 0xffffff;
    
    this.centerText = new PIXI.Text({
      text: this.config.ctaText || 'SPIN',
      style: {
        fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
        fontSize: 18,
        fontWeight: '900',
        fill: textColor,
        align: 'center',
        letterSpacing: 1.5,
        stroke: { color: 0xffffff, width: 2 },
        dropShadow: {
          color: 0x000000,
          alpha: 0.2,
          blur: 3,
          angle: Math.PI / 4,
          distance: 2
        }
      },
      resolution: 4 // High-res text
    });
    
    this.centerText.anchor.set(0.5);
    this.centerText.x = 0;
    this.centerText.y = 0;
    
    // Add to center base layer (beneath gloss)
    this.centerBaseLayer.addChild(this.centerText);
  }

  // ============================================================================
  // Animation
  // ============================================================================

  /**
   * Animate bulbs (breathing effect via alpha crossfade)
   */
  public animateBulbs(): void {
    if (!this.bulbsOnSprite) return;
    
    const time = Date.now() / 1000;
    const period = 1.8; // ~1.8 seconds per cycle
    
    // Oscillate bulbsOn alpha between 0.2 and 1.0
    const alpha = 0.2 + 0.8 * (0.5 + 0.5 * Math.sin((time / period) * Math.PI * 2));
    this.bulbsOnSprite.alpha = alpha;
  }

  // ============================================================================
  // Public API
  // ============================================================================

  /**
   * Get spinning container (for rotation)
   */
  public getSpinContainer(): PIXI.Container {
    return this.spinContainer;
  }

  /**
   * Set icon textures (loaded separately)
   */
  public setIconTextures(icons: IconTextures): void {
    this.iconTextures = icons;
    this.render(); // Re-render to show icons
  }

  /**
   * Update config and re-render
   */
  public updateConfig(config: WheelConfig): void {
    this.config = config;
    this.render();
  }

  /**
   * Highlight winning segment (glow effect)
   */
  public highlightWinningSegment(segmentIndex: number): void {
    const segments = this.config.segments;
    const totalWeight = segments.reduce((sum, seg) => sum + seg.weight, 0);
    let currentAngle = -Math.PI / 2;
    
    for (let i = 0; i < segments.length; i++) {
      const segmentAngle = (segments[i].weight / totalWeight) * Math.PI * 2;
      
      if (i === segmentIndex) {
        const midAngle = currentAngle + segmentAngle / 2;
        this.drawWinGlow(midAngle, segmentAngle);
        break;
      }
      
      currentAngle += segmentAngle;
    }
  }

  private drawWinGlow(midAngle: number, segmentAngle: number): void {
    const glow = new PIXI.Graphics();
    
    glow.arc(
      0,
      0,
      this.wheelRadius + 8,
      midAngle - segmentAngle / 2,
      midAngle + segmentAngle / 2
    );
    glow.stroke({ color: 0xFFDD00, width: 12, alpha: 0.9 });
    
    glow.filters = [new PIXI.BlurFilter({ strength: 16 })];
    
    this.spinContainer.addChild(glow);
    
    setTimeout(() => {
      this.spinContainer.removeChild(glow);
    }, 2500);
    
    // SPECTACULAR CONFETTI!
    this.launchConfetti();
  }

  /**
   * Launch SPECTACULAR confetti explosion - CASINO STYLE!
   */
  private launchConfetti(): void {
    const confettiColors = [
      0xFFD700, // Gold
      0xFFF700, // Bright Yellow
      0xFF6B6B, // Red
      0xFF1744, // Bright Red
      0x4ECDC4, // Teal
      0x00E5FF, // Cyan
      0xFFE66D, // Yellow
      0xF38181, // Coral
      0xAA96DA, // Purple
      0xE040FB, // Magenta
      0x69F0AE, // Green
      0xFFAB40, // Orange
    ];
    
    const confettiContainer = new PIXI.Container();
    this.addChild(confettiContainer);
    
    // ========== WAVE 1: MAIN EXPLOSION (150 particles) ==========
    const particles: Array<{
      sprite: PIXI.Graphics;
      vx: number;
      vy: number;
      rotation: number;
      gravity: number;
      scale: number;
      scaleDecay: number;
    }> = [];
    
    // Main explosion - 150 large particles
    for (let i = 0; i < 150; i++) {
      const confetti = new PIXI.Graphics();
      const color = confettiColors[Math.floor(Math.random() * confettiColors.length)];
      const size = 6 + Math.random() * 14; // Bigger particles
      
      // Various shapes
      const shapeType = Math.random();
      if (shapeType < 0.33) {
        // Rectangle
        confetti.rect(-size/2, -size/4, size, size/2);
      } else if (shapeType < 0.66) {
        // Circle
        confetti.circle(0, 0, size/2);
      } else {
        // Star/diamond
        confetti.moveTo(0, -size/2);
        confetti.lineTo(size/3, 0);
        confetti.lineTo(0, size/2);
        confetti.lineTo(-size/3, 0);
        confetti.closePath();
      }
      confetti.fill({ color, alpha: 1 });
      
      // Explosive start from center
      const angle = Math.random() * Math.PI * 2;
      const speed = 12 + Math.random() * 20; // Faster!
      
      confetti.position.set(0, -this.wheelRadius * 0.2);
      
      particles.push({
        sprite: confetti,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 8, // Strong upward boost
        rotation: (Math.random() - 0.5) * 0.4,
        gravity: 0.12 + Math.random() * 0.08,
        scale: 1,
        scaleDecay: 0.997
      });
      
      confettiContainer.addChild(confetti);
    }
    
    // ========== WAVE 2: GOLDEN SPARKLES (50 particles) ==========
    setTimeout(() => {
      for (let i = 0; i < 50; i++) {
        const sparkle = new PIXI.Graphics();
        const goldShade = [0xFFD700, 0xFFE135, 0xFFF44F, 0xFFDF00][Math.floor(Math.random() * 4)];
        
        // Star shape
        sparkle.star(0, 0, 5, 3 + Math.random() * 5, 1.5 + Math.random() * 2.5);
        sparkle.fill({ color: goldShade, alpha: 1 });
        
        const angle = Math.random() * Math.PI * 2;
        const speed = 8 + Math.random() * 12;
        
        sparkle.position.set(0, -this.wheelRadius * 0.2);
        
        particles.push({
          sprite: sparkle,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 6,
          rotation: (Math.random() - 0.5) * 0.5,
          gravity: 0.1,
          scale: 1,
          scaleDecay: 0.99
        });
        
        confettiContainer.addChild(sparkle);
      }
    }, 150);
    
    // ========== WAVE 3: SIDE BURSTS (40 particles each side) ==========
    setTimeout(() => {
      [-1, 1].forEach(dir => {
        for (let i = 0; i < 40; i++) {
          const confetti = new PIXI.Graphics();
          const color = confettiColors[Math.floor(Math.random() * confettiColors.length)];
          const size = 5 + Math.random() * 10;
          
          confetti.rect(-size/2, -size/4, size, size/2);
          confetti.fill({ color, alpha: 1 });
          
          const angle = (Math.random() * 0.8 - 0.4) + (dir > 0 ? 0 : Math.PI);
          const speed = 10 + Math.random() * 15;
          
          confetti.position.set(dir * this.wheelRadius * 0.5, -this.wheelRadius * 0.3);
          
          particles.push({
            sprite: confetti,
            vx: Math.cos(angle) * speed * dir,
            vy: Math.sin(angle) * speed - 4,
            rotation: (Math.random() - 0.5) * 0.3,
            gravity: 0.14,
            scale: 1,
            scaleDecay: 0.995
          });
          
          confettiContainer.addChild(confetti);
        }
      });
    }, 300);
    
    // Animate all particles
    let frame = 0;
    const maxFrames = 240; // ~4 seconds
    
    const animate = () => {
      frame++;
      
      particles.forEach(p => {
        p.sprite.x += p.vx;
        p.sprite.y += p.vy;
        p.vy += p.gravity;
        p.sprite.rotation += p.rotation;
        p.vx *= 0.99;
        p.scale *= p.scaleDecay;
        p.sprite.scale.set(p.scale);
        
        // Shimmer effect for gold particles
        if (Math.random() > 0.95) {
          p.sprite.alpha = 0.7 + Math.random() * 0.3;
        }
        
        // Fade out in last 80 frames
        if (frame > maxFrames - 80) {
          p.sprite.alpha = Math.max(0, p.sprite.alpha * 0.95);
        }
      });
      
      if (frame < maxFrames) {
        requestAnimationFrame(animate);
      } else {
        this.removeChild(confettiContainer);
        confettiContainer.destroy({ children: true });
      }
    };
    
    animate();
  }
}
